import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { DataTable, EmptyState, PageHeader, StatusChip } from '@routes-plan/ui';
import { SalesRep } from '../../../models/sales-rep';
import { Office } from '../../../models/office';
import { Route } from '../../../models/route';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { isSuperAdminUser } from '../../../utils/authScope';
import StandardModal, { useModalLoading } from '../../../components/modales/StandardModal';
import { setPageTitle } from '../../../utils/pageTitle';
import { useT } from '../../../i18n';
import MobileDevicesPanel, { MobileDevice, MobileDeviceRequest } from './MobileDevicesPanel';
import { AdminPage, ContentCard, FilterRow, RowAction, RowActions, useAlert, useConfirm } from '../shared/AdminUi';

type RowId = string | number;
interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

interface SalesRepRow {
    id: number;
    idNumber: string;
    fullName: string;
    username: string;
    mobilePhone: string;
    office: string;
    route: string;
    isUnassigned: boolean;
    sessionEnabled: boolean;
}

const SalesRepList: React.FC<ShowAlerts> = () => {
    const t = useT();
    const showAlert = useAlert();
    const [confirm, confirmDialog] = useConfirm();
    setPageTitle('salesReps.pageTitle');
    const [loading, setLoading] = useState(true);
    const [employees, setEmployees] = useState<SalesRep[]>([]);
    const [mobileModalOpen, setMobileModalOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<SalesRep | null>(null);
    const [mobileDevices, setMobileDevices] = useState<MobileDevice[]>([]);
    const [mobileDeviceRequests, setMobileDeviceRequests] = useState<MobileDeviceRequest[]>([]);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [assignEmployee, setAssignEmployee] = useState<SalesRep | null>(null);
    const [assignOfficeId, setAssignOfficeId] = useState<string>('');
    const [assignRouteId, setAssignRouteId] = useState<string>('');
    const [assignRoutes, setAssignRoutes] = useState<Route[]>([]);
    const [offices, setOffices] = useState<Office[]>([]);
    const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
    const [officeId, setOfficeId] = useState<string>('');
    const [routeId, setRouteId] = useState<string>('');
    const [showUnassigned, setShowUnassigned] = useState(false);
    const [officesLoaded, setOfficesLoaded] = useState(false);
    const {
        canCreate,
        canUpdate,
        canDelete,
        canManageMobile,
        canAssign,
        authUserData,
        isLoadingPermissions,
    } = useResourcePermissions('/admin/sales-reps');
    const isSuperAdmin = useMemo(() => isSuperAdminUser(authUserData), [authUserData]);

    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [passwordTarget, setPasswordTarget] = useState<{
        id: number;
        fullName: string;
        username: string;
    } | null>(null);
    const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
    const [passwordLoading, setPasswordLoading] = useState(false);
    // Cargando propio de cada modal (el general queda detrás de los modales).
    const mobileLoading = useModalLoading();
    const assignLoading = useModalLoading();

    const loadEmployees = async (selectedOfficeId?: string, selectedRouteId?: string) => {
        const { data } = await axios.get(`sales-reps`, {
            params: {
                ...(selectedOfficeId ? { officeId: selectedOfficeId } : {}),
                ...(selectedRouteId ? { routeId: selectedRouteId } : {}),
            },
        });
        setEmployees(Array.isArray(data?.data) ? data.data : []);
    };

    useEffect(() => {
        if (isLoadingPermissions || !officesLoaded) {
            return;
        }

        if (!isSuperAdmin && offices.length > 0 && !officeId) {
            return;
        }

        const fetchEmployeesByOffice = async () => {
            setLoading(true);
            try {
                await loadEmployees(officeId, routeId);
            } catch (error) {
                console.error(error);
                showAlert(t('salesReps.loadListError'), 'error');
            } finally {
                setLoading(false);
            }
        };

        void fetchEmployeesByOffice();
    }, [routeId, officeId, isSuperAdmin, isLoadingPermissions, offices.length, officesLoaded]);

    useEffect(() => {
        if (isLoadingPermissions) {
            return;
        }

        const fetchOffices = async () => {
            try {
                const officesUrl = isSuperAdmin ? 'offices/all' : 'offices';
                const { data } = await axios.get(officesUrl);
                const options = Array.isArray(data?.data) ? data.data : [];
                setOffices(options);
                if (options.length === 0) {
                    setOfficeId('');
                    setRouteId('');
                    return;
                }

                if (isSuperAdmin) {
                    setOfficeId('');
                    setRouteId('');
                    return;
                }

                const currentOfficeAllowed = options.some(
                    (office: Office) => String(office.id) === String(officeId)
                );

                if (!currentOfficeAllowed) {
                    setOfficeId(String(options[0].id));
                    setRouteId('');
                }
            } catch (error) {
                console.error(error);
                setOffices([]);
                setOfficeId('');
                setRouteId('');
            } finally {
                setOfficesLoaded(true);
            }
        };

        void fetchOffices();
    }, [isSuperAdmin, isLoadingPermissions]);

    useEffect(() => {
        const fetchRoutesByOffice = async () => {
            if (!officeId) {
                setOfficeRoutes([]);
                setRouteId('');
                return;
            }

            try {
                const { data } = await axios.get(`offices/${officeId}/routes`);
                const routes = Array.isArray(data) ? data : [];
                setOfficeRoutes(routes);
                if (isSuperAdmin) {
                    if (routeId && !routes.some((route: Route) => String(route.id) === String(routeId))) {
                        setRouteId('');
                    }
                    return;
                }

                if (routes.length === 0) {
                    setRouteId('');
                    return;
                }

                const currentRouteAllowed = routes.some(
                    (route: Route) => String(route.id) === String(routeId)
                );
                if (!currentRouteAllowed) {
                    setRouteId(String(routes[0].id));
                }
            } catch (error) {
                console.error(error);
                setOfficeRoutes([]);
                setRouteId('');
            }
        };

        fetchRoutesByOffice();
    }, [routeId, isSuperAdmin, officeId]);

    const loadEmployeeMobileData = async (employeeId: number) => {
        const [devicesResponse, requestsResponse] = await Promise.all([
            axios.get(`sales-reps/${employeeId}/mobile-devices`),
            axios.get(`sales-reps/${employeeId}/mobile-device-requests`),
        ]);

        setMobileDevices(Array.isArray(devicesResponse.data) ? devicesResponse.data : []);
        setMobileDeviceRequests(Array.isArray(requestsResponse.data) ? requestsResponse.data : []);
    };

    const handleOpenMobileModal = async (employeeId: RowId) => {
        if (!canManageMobile) return;

        const numericId = typeof employeeId === 'string' ? Number(employeeId) : employeeId;
        if (!Number.isFinite(numericId)) {
            showAlert(t('salesReps.invalidId'), 'error');
            return;
        }

        const employee = employees.find((item) => item.id === numericId) || null;
        setSelectedEmployee(employee);
        setMobileModalOpen(true);

        try {
            mobileLoading.showLoading();
            await loadEmployeeMobileData(numericId);
        } catch (error: any) {
            console.error(error);
            showAlert(t('salesReps.appLoadError'), 'error');
        } finally {
            mobileLoading.hideLoading();
        }
    };

    const handleCloseMobileModal = () => {
        setMobileModalOpen(false);
        setSelectedEmployee(null);
        setMobileDevices([]);
        setMobileDeviceRequests([]);
    };

    const getAssignedRouteIds = (excludeEmployeeId?: number) => {
        const assigned = new Set<number>();

        employees.forEach((employee) => {
            if (excludeEmployeeId && Number(employee.id) === Number(excludeEmployeeId)) {
                return;
            }

            getEmployeeRoutes(employee).forEach((route: any) => {
                const routeIdValue = Number(route?.id);
                if (Number.isFinite(routeIdValue)) {
                    assigned.add(routeIdValue);
                }
            });
        });

        return assigned;
    };

    const loadAssignableRoutes = async (officeValue: string, employee: SalesRep) => {
        if (!officeValue) {
            setAssignRoutes([]);
            setAssignRouteId('');
            return;
        }

        const currentRouteIds = new Set(
            getEmployeeRoutes(employee)
                .map((route: any) => Number(route?.id))
                .filter((id: number) => Number.isFinite(id))
        );
        const assignedRouteIds = getAssignedRouteIds(employee.id);
        const { data } = await axios.get(`offices/${officeValue}/routes`);
        const availableRoutes = (Array.isArray(data) ? data : []).filter((route: Route) => {
            const routeIdValue = Number(route?.id);
            if (!Number.isFinite(routeIdValue)) {
                return false;
            }

            if (currentRouteIds.has(routeIdValue)) {
                return true;
            }

            const directAssignedUserId = Number((route as any)?.user?.id ?? 0);
            if (directAssignedUserId > 0 && directAssignedUserId !== Number(employee.id)) {
                return false;
            }

            return !assignedRouteIds.has(routeIdValue);
        });

        setAssignRoutes(availableRoutes);

        const currentRouteId = Array.from(currentRouteIds).find((routeIdValue) =>
            availableRoutes.some((route) => Number(route.id) === Number(routeIdValue))
        );

        setAssignRouteId(
            currentRouteId
                ? String(currentRouteId)
                : availableRoutes.length > 0
                    ? String(availableRoutes[0].id)
                    : ''
        );
    };

    const handleOpenAssignModal = async (employeeId: RowId) => {
        if (!canAssign) return;

        const numericId = typeof employeeId === 'string' ? Number(employeeId) : employeeId;
        if (!Number.isFinite(numericId)) {
            showAlert(t('salesReps.invalidId'), 'error');
            return;
        }

        const employee = employees.find((item) => Number(item.id) === Number(numericId)) || null;
        if (!employee) {
            showAlert(t('salesReps.notFound'), 'error');
            return;
        }

        if (offices.length === 0) {
            showAlert(t('salesReps.noOfficesToAssign'), 'warning');
            return;
        }

        const employeeOffice = getEmployeeOffices(employee)[0];
        const preferredOfficeId = offices.some((office) => Number(office.id) === Number(employeeOffice?.id))
            ? String(employeeOffice?.id)
            : String(offices[0].id);

        setAssignEmployee(employee);
        setAssignModalOpen(true);
        setAssignOfficeId(preferredOfficeId);

        try {
            assignLoading.showLoading();
            await loadAssignableRoutes(preferredOfficeId, employee);
        } catch (error) {
            console.error(error);
            setAssignRoutes([]);
            setAssignRouteId('');
            showAlert(t('salesReps.routesLoadError'), 'error');
        } finally {
            assignLoading.hideLoading();
        }
    };

    const handleCloseAssignModal = () => {
        setAssignModalOpen(false);
        setAssignEmployee(null);
        setAssignOfficeId('');
        setAssignRouteId('');
        setAssignRoutes([]);
    };

    const handleAssignOfficeChange = async (nextOfficeId: string) => {
        setAssignOfficeId(nextOfficeId);

        if (!assignEmployee) {
            setAssignRoutes([]);
            setAssignRouteId('');
            return;
        }

        try {
            assignLoading.showLoading();
            await loadAssignableRoutes(nextOfficeId, assignEmployee);
        } catch (error) {
            console.error(error);
            setAssignRoutes([]);
            setAssignRouteId('');
            showAlert(t('salesReps.routesLoadError'), 'error');
        } finally {
            assignLoading.hideLoading();
        }
    };

    const handleSaveAssignedRoute = async () => {
        if (!canAssign || !assignEmployee?.id) return;

        if (!assignOfficeId || !assignRouteId) {
            showAlert(t('salesReps.selectOfficeAndRoute'), 'warning');
            return;
        }

        try {
            assignLoading.showLoading();
            const { data } = await axios.post(`sales-reps/${assignEmployee.id}/route`, {
                officeId: Number(assignOfficeId),
                routeId: Number(assignRouteId),
            });

            setEmployees((prev) =>
                prev.map((employee) =>
                    Number(employee.id) === Number(assignEmployee.id)
                        ? (data?.data as SalesRep)
                        : employee
                )
            );
            showAlert(data?.message || t('salesReps.routeAssignedOk'), 'success');
            handleCloseAssignModal();
        } catch (error: any) {
            console.error(error);
            showAlert(
                error?.response?.data?.message || t('salesReps.routeAssignError'),
                'error'
            );
        } finally {
            assignLoading.hideLoading();
        }
    };

    const handleMobileDeviceStatusChange = async (
        deviceId: number,
        status: 'approved' | 'blocked'
    ) => {
        if (!canManageMobile) return;
        if (!selectedEmployee?.id) return;

        try {
            mobileLoading.showLoading();
            const { data } = await axios.put(`mobile-devices/${deviceId}/status`, {
                status,
            });
            await loadEmployeeMobileData(selectedEmployee.id);
            showAlert(data.message || t('salesReps.deviceStatusUpdated'), 'success');
        } catch (error: any) {
            console.error(error);
            showAlert(
                error?.response?.data?.message || t('salesReps.deviceStatusError'),
                'error'
            );
        } finally {
            mobileLoading.hideLoading();
        }
    };

    const handleMobileDeviceRequestStatusChange = async (
        requestId: number,
        status: 'approved' | 'rejected'
    ) => {
        if (!canManageMobile) return;
        if (!selectedEmployee?.id) return;

        try {
            mobileLoading.showLoading();
            const { data } = await axios.put(`mobile-device-requests/${requestId}/status`, {
                status,
            });
            await loadEmployeeMobileData(selectedEmployee.id);
            showAlert(data.message || t('salesReps.requestUpdatedOk'), 'success');
        } catch (error: any) {
            console.error(error);
            showAlert(
                error?.response?.data?.message || t('salesReps.requestUpdateError'),
                'error'
            );
        } finally {
            mobileLoading.hideLoading();
        }
    };

    const handleOpenPasswordModal = (row: { id: RowId; fullName: string; username: string }) => {
        if (!isSuperAdmin) return;
        const numericId = typeof row.id === 'string' ? Number(row.id) : row.id;
        if (!Number.isFinite(numericId)) {
            showAlert(t('salesReps.invalidId'), 'error');
            return;
        }
        setPasswordTarget({
            id: numericId,
            fullName: row.fullName,
            username: row.username,
        });
        setGeneratedPassword(null);
        setPasswordModalOpen(true);
    };

    const handleClosePasswordModal = () => {
        setPasswordModalOpen(false);
        setPasswordTarget(null);
        setGeneratedPassword(null);
        setPasswordLoading(false);
    };

    const handleGenerateEmployeePassword = async () => {
        if (!passwordTarget?.id) return;
        setPasswordLoading(true);
        setGeneratedPassword(null);
        try {
            const { data } = await axios.post(
                `sales-reps/${passwordTarget.id}/regenerate-password`,
            );
            const plain = data?.data?.newPassword ?? '';
            setGeneratedPassword(plain);
            showAlert(data?.message || t('salesReps.passwordGeneratedOk'), 'success');
        } catch (error: any) {
            console.error(error);
            showAlert(
                error?.response?.data?.message || t('salesReps.passwordGenerateError'),
                'error',
            );
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleCopyGeneratedPassword = async () => {
        if (!generatedPassword) return;
        try {
            await navigator.clipboard.writeText(generatedPassword);
            showAlert(t('salesReps.passwordCopied'), 'info');
        } catch {
            showAlert(t('salesReps.copyError'), 'warning');
        }
    };

    const handleDeleteEmployee = async (row: SalesRepRow) => {
        if (!canDelete) return;
        const ok = await confirm({
            title: t('salesReps.confirmDeleteTitle', { name: row.fullName || row.username }),
            message: t('salesReps.confirmDelete'),
        });
        if (!ok) return;
        try {
            await axios.delete(`sales-reps/${row.id}`);
            setEmployees((prev) => prev.filter((e) => e.id !== row.id));
            showAlert(t('salesReps.deletedOk'), 'success');
        } catch (error: any) {
            console.error(error);
            showAlert(error?.response?.data?.message || t('salesReps.deleteError'), 'error');
        }
    };

    const getEmployeeRoutes = (employee: SalesRep) => {
        const directRoutes = Array.isArray(employee.routes) ? employee.routes : [];
        if (directRoutes.length > 0) {
            return directRoutes;
        }

        const userRoutes = Array.isArray((employee as any).userRoutes)
            ? (employee as any).userRoutes
            : [];

        return userRoutes
            .map((item: any) => item?.route)
            .filter((route: any) => Boolean(route?.id));
    };

    const getEmployeeOffices = (employee: SalesRep) => {
        const routeOffices = getEmployeeRoutes(employee)
            .map((route: any) => route?.office)
            .filter((office: any) => Boolean(office?.id));

        if (routeOffices.length > 0) {
            return routeOffices;
        }

        const userOffices = Array.isArray((employee as any).userOffices)
            ? (employee as any).userOffices
            : [];

        return userOffices
            .map((item: any) => item?.office)
            .filter((office: any) => Boolean(office?.id));
    };

    const isEmployeeUnassigned = (employee: SalesRep) =>
        getEmployeeRoutes(employee).length === 0 && getEmployeeOffices(employee).length === 0;

    const filteredEmployees = employees.filter((employee) => {
        const employeeRoutes = getEmployeeRoutes(employee);
        const employeeOffices = getEmployeeOffices(employee);
        const isUnassigned = isEmployeeUnassigned(employee);

        if (isSuperAdmin && showUnassigned) {
            return isUnassigned;
        }

        if (isSuperAdmin && !officeId) {
            if (routeId) {
                return employeeRoutes.some((route: any) => String(route?.id) === String(routeId));
            }
            return true;
        }

        if (!officeId) return false;
        if (routeId) {
            return employeeRoutes.some((route: any) => String(route?.id) === String(routeId));
        }
        return employeeOffices.some((office: any) => String(office?.id) === String(officeId));
    });

    const employeesForTable = isSuperAdmin
        ? [...filteredEmployees].sort((a, b) => {
            const oa =
                getEmployeeOffices(a)[0]?.name ??
                (isEmployeeUnassigned(a) ? t('salesReps.noOffice') : '');
            const ob =
                getEmployeeOffices(b)[0]?.name ??
                (isEmployeeUnassigned(b) ? t('salesReps.noOffice') : '');
            const byOffice = String(oa).localeCompare(String(ob), 'es', { sensitivity: 'base' });
            if (byOffice !== 0) return byOffice;
            const ca =
                getEmployeeRoutes(a)[0]?.name ??
                (isEmployeeUnassigned(a) ? t('salesReps.noRoute') : '');
            const cb =
                getEmployeeRoutes(b)[0]?.name ??
                (isEmployeeUnassigned(b) ? t('salesReps.noRoute') : '');
            return String(ca).localeCompare(String(cb), 'es', { sensitivity: 'base' });
        })
        : filteredEmployees;

    const rows: SalesRepRow[] = employeesForTable.map((employee) => {
        const employeeRoutes = getEmployeeRoutes(employee);
        const employeeOffices = getEmployeeOffices(employee);
        const firstRoute = employeeRoutes.length > 0 ? employeeRoutes[0] : null;
        const firstOffice = employeeOffices.length > 0 ? employeeOffices[0] : null;
        const isUnassigned = isEmployeeUnassigned(employee);

        return {
            id: Number(employee.id),
            idNumber: String(employee.idNumber ?? ''),
            fullName: `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim(),
            username: employee.username ?? '',
            mobilePhone: String(employee.mobilePhone ?? ''),
            office: firstOffice?.name ?? (isUnassigned ? t('salesReps.noOffice') : ''),
            route: firstRoute?.name ?? (isUnassigned ? t('salesReps.noRoute') : ''),
            isUnassigned,
            sessionEnabled: Number(employee.mobileSessionEnabled ?? 1) === 1,
        };
    });

    const addButton = (
        <PermissionButton
            allowed={canCreate}
            deniedTooltip={t('common.noPermissionCreate')}
            variant="contained"
            startIcon={<AddIcon />}
            component={NavLink}
            to="/admin/sales-reps/new"
        >
            {t('salesReps.createButton')}
        </PermissionButton>
    );

    return (
        <AdminPage>
            <PageHeader title={t('salesReps.title')} description={t('salesReps.subtitle')} actions={addButton} />
            <ContentCard>
                <FilterRow sx={{ mb: 2 }}>
                    <TextField
                        id="employees-office-filter"
                        select
                        size="small"
                        label={t('salesReps.office')}
                        value={offices.length ? officeId : ''}
                        onChange={(event) => {
                            setOfficeId(String(event.target.value));
                            setRouteId('');
                        }}
                        SelectProps={{ displayEmpty: isSuperAdmin }}
                        InputLabelProps={{ shrink: isSuperAdmin || !!officeId }}
                        fullWidth
                    >
                        {isSuperAdmin && <MenuItem value="">{t('salesReps.allOffices')}</MenuItem>}
                        {offices.map((office: Office) => (
                            <MenuItem key={office.id} value={String(office.id)}>
                                {office.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        id="employees-route-filter"
                        select
                        size="small"
                        label={t('salesReps.route')}
                        value={officeRoutes.length ? routeId : ''}
                        onChange={(event) => setRouteId(String(event.target.value))}
                        disabled={!officeId}
                        fullWidth
                    >
                        {officeId &&
                            officeRoutes.map((route: Route) => (
                                <MenuItem key={route.id} value={String(route.id)}>
                                    {route.name}
                                </MenuItem>
                            ))}
                    </TextField>
                    {isSuperAdmin && (
                        <FormControlLabel
                            control={<Checkbox checked={showUnassigned} onChange={(event) => setShowUnassigned(event.target.checked)} />}
                            label={t('salesReps.showUnassigned')}
                        />
                    )}
                </FilterRow>
                <DataTable<SalesRepRow>
                    rows={rows}
                    loading={loading}
                    getRowId={(r) => r.id}
                    ariaLabel={t('salesReps.title')}
                    searchable
                    searchPlaceholder={t('salesReps.searchPlaceholder')}
                    exportFileName={t('salesReps.exportFileName')}
                    columns={[
                        { id: 'fullName', header: t('salesReps.fullName'), value: (r) => r.fullName, sortable: true },
                        { id: 'username', header: t('common.username'), value: (r) => r.username, sortable: true, hideBelow: 'sm' },
                        { id: 'idNumber', header: t('common.idNumber'), value: (r) => r.idNumber, sortable: true, hideBelow: 'md' },
                        { id: 'mobilePhone', header: t('common.cellphone'), value: (r) => r.mobilePhone, hideBelow: 'md' },
                        {
                            id: 'office',
                            header: t('salesReps.office'),
                            value: (r) => r.office,
                            render: (r) => (r.isUnassigned ? <StatusChip label={r.office} tone="warning" /> : r.office),
                            sortable: true,
                        },
                        { id: 'route', header: t('salesReps.route'), value: (r) => r.route, sortable: true },
                        {
                            id: 'session',
                            header: t('salesReps.appSession'),
                            value: (r) => (r.sessionEnabled ? t('salesReps.sessionEnabled') : t('salesReps.sessionDisabled')),
                            render: (r) => (
                                <StatusChip
                                    label={r.sessionEnabled ? t('salesReps.sessionEnabled') : t('salesReps.sessionDisabled')}
                                    tone={r.sessionEnabled ? 'success' : 'neutral'}
                                />
                            ),
                            sortable: true,
                            hideBelow: 'sm',
                        },
                    ]}
                    rowActions={(row) => (
                        <RowActions>
                            {row.isUnassigned && (
                                <RowAction
                                    label={`${t('salesReps.assignRoute')}: ${row.fullName}`}
                                    icon={<AltRouteOutlinedIcon fontSize="small" />}
                                    color="primary"
                                    allowed={canAssign}
                                    deniedTooltip={t('salesReps.noPermissionAssignRoute')}
                                    onClick={() => handleOpenAssignModal(row.id)}
                                />
                            )}
                            <RowAction
                                label={`${t('salesReps.manageApp')}: ${row.fullName}`}
                                icon={<PhoneAndroidOutlinedIcon fontSize="small" />}
                                allowed={canManageMobile}
                                deniedTooltip={t('salesReps.noPermissionManageApp')}
                                onClick={() => handleOpenMobileModal(row.id)}
                            />
                            {isSuperAdmin && (
                                <RowAction
                                    label={`${t('salesReps.generatePasswordTooltip')}: ${row.fullName}`}
                                    icon={<KeyOutlinedIcon fontSize="small" />}
                                    onClick={() => handleOpenPasswordModal(row)}
                                />
                            )}
                            <RowAction
                                label={`${t('common.edit')} ${row.fullName}`}
                                icon={<EditOutlinedIcon fontSize="small" />}
                                allowed={canUpdate}
                                deniedTooltip={t('common.noPermissionEdit')}
                                to={`/admin/sales-reps/${row.id}/edit`}
                            />
                            <RowAction
                                label={`${t('common.delete')} ${row.fullName}`}
                                icon={<DeleteOutlineIcon fontSize="small" />}
                                color="error"
                                allowed={canDelete}
                                deniedTooltip={t('common.noPermissionDelete')}
                                onClick={() => handleDeleteEmployee(row)}
                            />
                        </RowActions>
                    )}
                    emptyState={
                        <EmptyState
                            icon={<BadgeOutlinedIcon />}
                            title={t('salesReps.emptyTitle')}
                            description={t('salesReps.emptyDescription')}
                            action={canCreate ? addButton : undefined}
                        />
                    }
                />
            </ContentCard>
            <StandardModal
                open={assignModalOpen}
                onClose={handleCloseAssignModal}
                title={t('salesReps.assignRoute')}
                subtitle={assignEmployee ? `${assignEmployee.firstName} ${assignEmployee.lastName} · ${assignEmployee.username}` : t('common.salesRep')}
                maxWidth={560}
                loading={assignLoading.loading}
                actions={
                    <>
                        <Button size="small" variant="outlined" color="secondary" onClick={handleCloseAssignModal}>
                            {t('common.cancel')}
                        </Button>
                        <PermissionButton
                            allowed={canAssign}
                            deniedTooltip={t('salesReps.noPermissionAssignRoute')}
                            tooltip={t('common.save')}
                            variant="contained"
                            size="small"
                            onClick={handleSaveAssignedRoute}
                            disabled={!assignRouteId}
                        >
                            {t('common.save')}
                        </PermissionButton>
                    </>
                }
            >
                <Stack spacing={2.5}>
                    <TextField
                        id="assign-office"
                        select
                        size="small"
                        label={t('salesReps.office')}
                        value={offices.length ? assignOfficeId : ''}
                        onChange={(event) => handleAssignOfficeChange(String(event.target.value))}
                        fullWidth
                    >
                        {offices.map((office: Office) => (
                            <MenuItem key={office.id} value={String(office.id)}>
                                {office.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        id="assign-route"
                        select
                        size="small"
                        label={t('salesReps.route')}
                        value={assignRoutes.length ? assignRouteId : ''}
                        onChange={(event) => setAssignRouteId(String(event.target.value))}
                        disabled={!assignOfficeId}
                        fullWidth
                    >
                        {assignRoutes.length === 0 ? (
                            <MenuItem value="" disabled>
                                {t('salesReps.noFreeRoutes')}
                            </MenuItem>
                        ) : (
                            assignRoutes.map((route: Route) => (
                                <MenuItem key={route.id} value={String(route.id)}>
                                    {route.name}
                                </MenuItem>
                            ))
                        )}
                    </TextField>

                    <Typography variant="body2" color="text.secondary">
                        {t('salesReps.assignRouteHint')}
                    </Typography>
                </Stack>
            </StandardModal>
            <StandardModal
                open={mobileModalOpen}
                onClose={handleCloseMobileModal}
                title={t('salesReps.manageAppTitle')}
                subtitle={selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName} · ${selectedEmployee.username}` : t('common.salesRep')}
                maxWidth={800}
                loading={mobileLoading.loading}
                actions={
                    <>
                        <span />
                        <Button size="small" variant="contained" onClick={handleCloseMobileModal}>
                            {t('common.close')}
                        </Button>
                    </>
                }
            >
                <MobileDevicesPanel
                    devices={mobileDevices}
                    requests={mobileDeviceRequests}
                    onDeviceStatus={handleMobileDeviceStatusChange}
                    onRequestStatus={handleMobileDeviceRequestStatusChange}
                    onlyPendingRequests
                />
            </StandardModal>

            <StandardModal
                open={passwordModalOpen}
                onClose={handleClosePasswordModal}
                title={t('salesReps.passwordModalTitle')}
                subtitle={passwordTarget ? `${passwordTarget.fullName} · ${passwordTarget.username}` : undefined}
                maxWidth={520}
                loading={passwordLoading}
                actions={
                    <>
                        <span />
                        <Button size="small" variant="contained" onClick={handleClosePasswordModal}>
                            {t('common.close')}
                        </Button>
                    </>
                }
            >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('salesReps.passwordModalInfo')}
                </Typography>

                {!generatedPassword ? (
                    <Button
                        variant="contained"
                        startIcon={<KeyOutlinedIcon />}
                        disabled={passwordLoading}
                        onClick={handleGenerateEmployeePassword}
                    >
                        {passwordLoading ? t('salesReps.generating') : t('salesReps.generatePassword')}
                    </Button>
                ) : (
                    <Stack spacing={2}>
                        <TextField
                            label={t('salesReps.newPassword')}
                            value={generatedPassword}
                            fullWidth
                            InputProps={{ readOnly: true }}
                            size="small"
                            sx={{ '& input': { fontFamily: 'monospace', letterSpacing: '0.04em' } }}
                        />
                        <Button variant="outlined" onClick={handleCopyGeneratedPassword}>
                            {t('salesReps.copyToClipboard')}
                        </Button>
                    </Stack>
                )}
            </StandardModal>
            {confirmDialog}
        </AdminPage>
    );
};

export default SalesRepList;
